import util from '../../../shared/util/util';
import {ESTIMATION_UNIT, projectStatusColors} from '../../../../constants';

export const getSortableProjectId = projectNode => {
	let sortableId = projectNode.customProjectId
		? projectNode.customProjectId
		: projectNode.companyProjectId !== undefined
		? projectNode.companyProjectId.toString()
		: projectNode.companyProjectGroupId?.toString();

	// pad the numeric component of the id with zeros, keep the initial string if it exists
	if (sortableId?.match(/^\D*\d+$/)) {
		const numberComponent = sortableId.match(/^\D*(\d+)$/)[1];
		const stringComponent = sortableId.replace(numberComponent, '');
		sortableId = stringComponent + String(numberComponent).padStart(8, '0');
	}
	return sortableId;
};

export const sortProjects = (projects, sortBy, calculatedDataMap) => {
	if (sortBy.column) {
		const sortConnectedProjects = (a, b, sortFunction) => {
			if (a.node.projects) {
				a.node.projects.edges.sort(sortFunction);
			}
			if (b.node.projects) {
				b.node.projects.edges.sort(sortFunction);
			}
		};

		const sortByNumericColumn = (a, b) => {
			sortConnectedProjects(a, b, sortByNumericColumn);

			if (a.node[sortBy.column] < b.node[sortBy.column]) return sortBy.ascending ? -1 : 1;
			if (a.node[sortBy.column] === b.node[sortBy.column]) return 0;
			if (a.node[sortBy.column] > b.node[sortBy.column]) return sortBy.ascending ? 1 : -1;
		};

		const sortByCustomColumn = (a, b, columnName) => {
			sortConnectedProjects(a, b, sortByCustomColumn);

			if (a.node[columnName] < b.node[columnName]) return sortBy.ascending ? -1 : 1;
			if (a.node[columnName] === b.node[columnName]) return 0;
			if (a.node[columnName] > b.node[columnName]) return sortBy.ascending ? 1 : -1;
		};

		const sortByIdColumn = (a, b) => {
			sortConnectedProjects(a, b, sortByIdColumn);
			let aId = getSortableProjectId(a.node);
			let bId = getSortableProjectId(b.node);

			if (aId < bId) return sortBy.ascending ? -1 : 1;
			if (aId === bId) return 0;
			if (aId > bId) return sortBy.ascending ? 1 : -1;
		};

		const sortByClientColumn = (a, b) => {
			sortConnectedProjects(a, b, sortByClientColumn);
			const aName = a.node.client ? a.node.client.name : '';
			const bName = b.node.client ? b.node.client.name : '';
			return util.compareLocaleNames(aName, bName) * (sortBy.ascending ? 1 : -1);
		};

		const sortByPriorityLevel = (a, b) => {
			sortConnectedProjects(a, b, sortByPriorityLevel);
			const aWeight = a.node.priorityLevel ? a.node.priorityLevel.weight : -1;
			const bWeight = b.node.priorityLevel ? b.node.priorityLevel.weight : -1;
			if (aWeight < bWeight) return sortBy.ascending ? -1 : 1;
			if (aWeight > bWeight) return sortBy.ascending ? 1 : -1;
			return 0;
		};

		const sortByNameColumn = (a, b) => {
			sortConnectedProjects(a, b, sortByNameColumn);
			const aName = a.node.name ? a.node.name.toUpperCase() : '';
			const bName = b.node.name ? b.node.name.toUpperCase() : '';
			if (aName < bName) return sortBy.ascending ? -1 : 1;
			if (aName > bName) return sortBy.ascending ? 1 : -1;
			return 0;
		};

		const sortByStartDate = (a, b) => {
			sortConnectedProjects(a, b, sortByStartDate);
			// connected projects should be compared by earliest start date
			let date_a, date_b;
			if (a.node.projects) {
				date_a = a.node.startDate;
			} else {
				date_a = util.CreateNonUtcMomentDate(a.node.projectStartYear, a.node.projectStartMonth, a.node.projectStartDay);
			}
			if (b.node.projects) {
				date_b = b.node.startDate;
			} else {
				date_b = util.CreateNonUtcMomentDate(b.node.projectStartYear, b.node.projectStartMonth, b.node.projectStartDay);
			}
			if (date_a < date_b) return sortBy.ascending ? -1 : 1;
			if (date_a === date_b) return 0;
			if (date_a > date_b) return sortBy.ascending ? 1 : -1;
		};

		const sortByEndDate = (a, b) => {
			sortConnectedProjects(a, b, sortByEndDate);
			// connected projects should be compared by latest end date
			let date_a, date_b;
			if (a.node.projects) {
				date_a = a.node.endDate;
			} else {
				date_a = util.CreateNonUtcMomentDate(a.node.projectEndYear, a.node.projectEndMonth, a.node.projectEndDay);
			}
			if (b.node.projects) {
				date_b = b.node.endDate;
			} else {
				date_b = util.CreateNonUtcMomentDate(b.node.projectEndYear, b.node.projectEndMonth, b.node.projectEndDay);
			}

			if (date_a < date_b) return sortBy.ascending ? -1 : 1;
			if (date_a === date_b) return 0;
			if (date_a > date_b) return sortBy.ascending ? 1 : -1;
		};

		const sortByCalculatedNumericColumn = (a, b, colName) => {
			const columnName = colName || sortBy.column;
			// sort projects in connected project
			if (a.node.projects) {
				a.node.projects.edges.sort((a, b) => sortByCalculatedNumericColumn(a, b, colName));
			}
			if (b.node.projects) {
				b.node.projects.edges.sort((a, b) => sortByCalculatedNumericColumn(a, b, colName));
			}

			const defaultValues = {completion: 0, tasksCount: 0, doneTasksCount: 0, projectPersonsCount: 0, reported: 0};

			const aNode = a.node.projects ? a.node : calculatedDataMap.get(a.node.id) || defaultValues;
			const bNode = b.node.projects ? b.node : calculatedDataMap.get(b.node.id) || defaultValues;

			if (aNode[columnName] < bNode[columnName]) return sortBy.ascending ? -1 : 1;
			if (aNode[columnName] === bNode[columnName]) return 0;
			if (aNode[columnName] > bNode[columnName]) return sortBy.ascending ? 1 : -1;
		};

		const sortByStatusColor = (a, b) => {
			sortConnectedProjects(a, b, sortByStatusColor);
			const aIndex = a.node.currentProjectStatus ? projectStatusColors.indexOf(a.node.currentProjectStatus.color) : -1;
			const bIndex = b.node.currentProjectStatus ? projectStatusColors.indexOf(b.node.currentProjectStatus.color) : -1;
			if (aIndex < bIndex) return sortBy.ascending ? 1 : -1;
			if (aIndex > bIndex) return sortBy.ascending ? -1 : 1;
		};

		const sortByContactPerson = (a, b) => {
			sortConnectedProjects(a, b, sortByContactPerson);
			let aValue = '';
			let bValue = '';
			if (a.node.projectPersons) {
				a.node.projectPersons.edges
					.filter(pp => pp.node.isContactPerson)
					.forEach(
						projectPerson =>
							(aValue +=
								projectPerson.node.person.firstName.toLowerCase() +
								projectPerson.node.person.lastName.toLowerCase())
					);
			}
			if (b.node.projectPersons) {
				b.node.projectPersons.edges
					.filter(pp => pp.node.isContactPerson)
					.forEach(
						projectPerson =>
							(bValue +=
								projectPerson.node.person.firstName.toLowerCase() +
								projectPerson.node.person.lastName.toLowerCase())
					);
			}
			if (aValue > bValue) return sortBy.ascending ? 1 : -1;
			if (aValue === bValue) return 0;
			if (aValue < bValue) return sortBy.ascending ? -1 : 1;
		};

		const sortByLabels = (a, b) => {
			sortConnectedProjects(a, b, sortByLabels);
			let aValue = '';
			let bValue = '';
			if (a.node.projectLabels) {
				a.node.projectLabels.edges.forEach(
					projectlabel => (aValue += projectlabel.node.label.name ? projectlabel.node.label.name.toLowerCase() : '')
				);
			}
			if (b.node.projectLabels) {
				b.node.projectLabels.edges.forEach(
					projectlabel => (bValue += projectlabel.node.label.name ? projectlabel.node.label.name.toLowerCase() : '')
				);
			}
			if (aValue > bValue) return sortBy.ascending ? 1 : -1;
			if (aValue === bValue) return 0;
			if (aValue < bValue) return sortBy.ascending ? -1 : 1;
		};

		const sortByVarianceToForecast = (a, b) => {
			sortConnectedProjects(a, b, sortByVarianceToForecast);
			const isAHourEstimated =
				a.node.isHourEstimated !== undefined ? a.node.isHourEstimated : a.node.estimationUnit === ESTIMATION_UNIT.HOURS;
			const isBHourEstimated =
				b.node.isHourEstimated !== undefined ? b.node.isHourEstimated : b.node.estimationUnit === ESTIMATION_UNIT.HOURS;

			const defaultValues = {forecast: 0, reported: 0, remaining: 0, minutesPerEstimationPoint: 1};

			const aNode = a.node.projects ? a.node : calculatedDataMap.get(a.node.id) || defaultValues;
			const bNode = b.node.projects ? b.node : calculatedDataMap.get(b.node.id) || defaultValues;

			let aValue = util.calculateVarianceToForecast(aNode, isAHourEstimated),
				bValue = util.calculateVarianceToForecast(bNode, isBHourEstimated);
			if (isAHourEstimated) {
				aValue /= 60.0;
			}
			if (isBHourEstimated) {
				bValue /= 60.0;
			}
			if (aValue < bValue) return sortBy.ascending ? -1 : 1;
			if (aValue === bValue) return 0;
			if (aValue > bValue) return sortBy.ascending ? 1 : -1;
		};

		const sortByHoursColumn = (a, b) => {
			sortConnectedProjects(a, b, sortByHoursColumn);

			const defaultValues = {forecast: 0, remaining: 0};

			const aNode = a.node.projects ? a.node : calculatedDataMap.get(a.node.id) || defaultValues;
			const bNode = b.node.projects ? b.node : calculatedDataMap.get(b.node.id) || defaultValues;

			let aValue = aNode[sortBy.column],
				bValue = bNode[sortBy.column];
			const isAHourEstimated =
				a.node.isHourEstimated !== undefined ? a.node.isHourEstimated : a.node.estimationUnit === ESTIMATION_UNIT.HOURS;
			const isBHourEstimated =
				b.node.isHourEstimated !== undefined ? b.node.isHourEstimated : b.node.estimationUnit === ESTIMATION_UNIT.HOURS;
			if (isAHourEstimated) {
				aValue /= 60.0;
			}
			if (isBHourEstimated) {
				bValue /= 60.0;
			}

			if (aValue < bValue) return sortBy.ascending ? -1 : 1;
			if (aValue === bValue) return 0;
			if (aValue > bValue) return sortBy.ascending ? 1 : -1;
		};

		if (sortBy.column === 'id') {
			projects = projects.sort(sortByIdColumn);
		} else if (sortBy.column === 'client') {
			projects = projects.sort(sortByClientColumn);
		} else if (sortBy.column === 'priorityLevel') {
			projects = projects.sort(sortByPriorityLevel);
		} else if (sortBy.column === 'name') {
			projects = projects.sort(sortByNameColumn);
		} else if (sortBy.column === 'startDate') {
			projects = projects.sort(sortByStartDate);
		} else if (sortBy.column === 'endDate') {
			projects = projects.sort(sortByEndDate);
		} else if (sortBy.column === 'variance_to_forecast') {
			projects = projects.sort(sortByVarianceToForecast);
		} else if (sortBy.column === 'statusColor' || sortBy.column === 'statusColorV2') {
			projects = projects.sort(sortByStatusColor);
		} else if (sortBy.column === 'contact') {
			projects = projects.sort(sortByContactPerson);
		} else if (sortBy.column === 'labels') {
			projects = projects.sort(sortByLabels);
		} else if (['forecast', 'remaining'].includes(sortBy.column)) {
			//need separate sort by function for columns using hours/points in order to convert points and hours so they can be compared correctly
			projects = projects.sort(sortByHoursColumn);
		} else if (['completion', 'tasksCount', 'doneTasksCount', 'projectPersonsCount', 'reported'].includes(sortBy.column)) {
			projects = projects.sort(sortByCalculatedNumericColumn);
		} else if (sortBy.column === 'win-chance') {
			projects = projects.sort((a, b) => sortByCustomColumn(a, b, 'baselineWinChance'));
		} else {
			projects = projects.sort(sortByNumericColumn);
		}
	}
	return projects;
};
